import { useImmerReducer } from "use-immer";
import { useContext, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import qs from "qs";
import {
  DEFAULT_PAGINATION,
  errorMessage,
  successMessage,
} from "../../../utils";
import { TableStateContext } from "../../../store";
import { deleteSegmentApi, getSegments } from "../api";
import { useAlertContext } from "../../../shared";
import { Button, Stack, Typography } from "@mui/material";

// Unique table identifier for state persistence
const tableName = "customer-segmentation";

// Action types
const ACTION_TYPES = {
  SET_TABLE_LOADING: "SET_TABLE_LOADING",
  SET_SEGMENTS: "SET_SEGMENTS",
  SET_SEARCH: "SET_SEARCH",
  SET_GOTO_PAGE: "SET_GOTO_PAGE",
  SET_PAGE_SIZE: "SET_PAGE_SIZE",
  SET_DELETE_ALERT: "SET_DELETE_ALERT",
  RESET_DELETE_ALERT: "RESET_DELETE_ALERT",
};

// Reducer function
const reducer = (draft, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_TABLE_LOADING:
      draft.tableLoading = action.payload;
      break;
    case ACTION_TYPES.SET_SEGMENTS:
      draft.segments = action.payload.segments;
      draft.pagination = action.payload.pagination;
      break;
    case ACTION_TYPES.SET_SEARCH:
      draft.search = action.payload;
      draft.pagination.pageIndex = 1; // Reset to first page on search
      break;
    case ACTION_TYPES.SET_GOTO_PAGE:
      draft.pagination.pageIndex = action.payload;
      break;
    case ACTION_TYPES.SET_PAGE_SIZE:
      draft.pagination.pageIndex = 1; // Reset to first page on page size change
      draft.pagination.pageSize = action.payload;
      break;
    case ACTION_TYPES.SET_DELETE_ALERT:
      draft.deleteSegment = action.payload;
      break;
    case ACTION_TYPES.RESET_DELETE_ALERT:
      draft.deleteSegment = null;
      break;
    default:
      break;
  }
};

export const useCustomerSegmentation = ({ list = true }) => {
  // Access table state context for persistence
  const { filterState, filterStateDispatch } = useContext(TableStateContext);
  const storedSearch = filterState.params[tableName]?.search;
  const storedPagination = filterState.params[tableName]?.pagination;

  // Initialize state with stored values or defaults
  const [state, dispatch] = useImmerReducer(reducer, {
    tableLoading: false,
    segments: [],
    search: storedSearch ?? "",
    pagination: storedPagination ?? DEFAULT_PAGINATION,
    deleteSegment: null,
  });

  // Breadcrumb navigation links
  const breadcrumbLinks = [
    { name: "Dashboard", href: "/" },
    { name: "Customer Segments" },
  ];

  /**
   * Build query parameters for API request
   */
  const getRequestUtilities = (currentState) => {
    const { pagination } = currentState;
    const params = {
      page: pagination.pageIndex,
      per_page: pagination.pageSize,
    };

    // Add search parameter if present
    if (currentState.search) {
      params.search = currentState.search;
    }

    return params;
  };

  /**
   * Fetch segments list from API
   */
  const getSegmentsList = async (currentState = state) => {
    const fallbackMessage = "Failed to fetch customer segments.";
    dispatch({ type: ACTION_TYPES.SET_TABLE_LOADING, payload: true });

    try {
      const params = getRequestUtilities(currentState);
      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params),
      };

      const response = await getSegments(query, currentState.pagination);
      const { success, message, data } = response;

      if (success) {
        // Persist state to TableStateContext for navigation
        filterStateDispatch({
          type: "SET_DATA",
          page: tableName,
          data: {
            search: currentState.search,
            pagination: data.pagination,
          },
        });

        // Update local state with new data
        dispatch({
          type: ACTION_TYPES.SET_SEGMENTS,
          payload: {
            segments: data.segments || [],
            pagination: data.pagination || currentState.pagination,
          },
        });
      } else {
        errorMessage(message || fallbackMessage);
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error?.message || fallbackMessage;
      errorMessage(message);
    } finally {
      dispatch({ type: ACTION_TYPES.SET_TABLE_LOADING, payload: false });
    }
  };

  /**
   * Delete a segment by ID and refresh the list
   */
  const deleteSegment = async (id) => {
    const fallbackMessage = "Failed to delete segment.";
    dispatch({ type: ACTION_TYPES.SET_DELETE_ALERT, payload: id });
    try {
      const response = await deleteSegmentApi(id);
      const { success, message } = response;
      if (success) {
        successMessage(message);
        getSegmentsList(); // Refresh list after deletion
      } else {
        errorMessage(message || fallbackMessage);
        return;
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error?.message || fallbackMessage;
      errorMessage(message);
    } finally {
      dispatch({ type: ACTION_TYPES.RESET_DELETE_ALERT });
    }
  };

  /**
   * Initial load when permission becomes available
   */
  useEffect(() => {
    list && getSegmentsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle pagination - go to specific page
   */
  const gotoPage = async (pageIndex) => {
    dispatch({ type: ACTION_TYPES.SET_GOTO_PAGE, payload: pageIndex });

    const newState = {
      ...state,
      pagination: { ...state.pagination, pageIndex },
    };

    await getSegmentsList(newState);
  };

  /**
   * Handle page size change
   */
  const setPageSize = async (e) => {
    const newPageSize = +e.target.value;
    dispatch({ type: ACTION_TYPES.SET_PAGE_SIZE, payload: newPageSize });

    const newState = {
      ...state,
      pagination: { ...state.pagination, pageSize: newPageSize, pageIndex: 1 },
    };

    await getSegmentsList(newState);
  };

  /**
   * Handle search input with debounce (1 second delay)
   */
  const debouncedSearch = useDebouncedCallback((searchText) => {
    const newState = {
      ...state,
      search: searchText,
      pagination: { ...state.pagination, pageIndex: 1 },
    };

    getSegmentsList(newState);
  }, 1000);

  /**
   * Handle search input change
   */
  const onChangeFilter = (value) => {
    const searchText = value;
    dispatch({ type: ACTION_TYPES.SET_SEARCH, payload: searchText });
    debouncedSearch(searchText);
  };

  /**
   * Clear search and reset to first page
   */
  const onSearchClear = async () => {
    dispatch({ type: ACTION_TYPES.SET_SEARCH, payload: "" });

    const newState = {
      ...state,
      search: "",
      pagination: { ...state.pagination, pageIndex: 1 },
    };

    await getSegmentsList(newState);
  };

  const { setAlert } = useAlertContext();
  const handleCloseDialog = () => {
    setAlert((draft) => {
      draft.open = false;
    });
  };
  /**
   * Open delete confirmation dialog
   */
  const toggleDeleteDialog = (segment, callBack = null) => {
    const couponCount = segment?.coupon_count || 0;
    const pushNotificationCount = segment?.push_notification_count || 0;
    const pointPromotionCount = segment.points_promotion_count || 0;
    const canDelete = couponCount === 0 && pointPromotionCount === 0;
    const hasPushNotification = pushNotificationCount > 0;

    const getNotificationText = (count, withPronoun = false) =>
      count === 1
        ? withPronoun
          ? "the notification"
          : "notification"
        : withPronoun
          ? "these notifications"
          : "notifications";

    const pluralize = (count, text) =>
      `${count} ${text}${count !== 1 ? "s" : ""}`;

    const getPreventDialogMessage = ({ pointPromotionCount, couponCount }) => {
      if (pointPromotionCount > 0 && couponCount > 0) {
        return `This segment cannot be deleted because it is currently being used by ${pluralize(couponCount, "coupon")}
        and ${pluralize(pointPromotionCount, "point promotion")} in active, paused, or draft status.`;
      }

      if (pointPromotionCount > 0) {
        return `This segment cannot be deleted because it is currently being used by ${pluralize(pointPromotionCount, "point promotion")} in active, paused, or draft status.`;
      }

      return `This segment cannot be deleted because it is currently being used by ${pluralize(couponCount, "coupon")} in active, paused, or draft status.`;
    };

    const message = !canDelete ? (
      getPreventDialogMessage({ pointPromotionCount, couponCount })
    ) : hasPushNotification ? (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 16,
        }}
      >
        This segment is currently being used in {pushNotificationCount} active
        push {getNotificationText(pushNotificationCount)}. Deleting this segment
        will also cancel &nbsp;
        {getNotificationText(pushNotificationCount, true)}. <br />
        Do you want to continue?
      </Typography>
    ) : (
      "Are you sure you want to delete this segment?"
    );

    setAlert((draft) => {
      draft.open = true;
      draft.title = "Delete Segment?";
      draft.description = message;
      draft.rowAction = (
        <Stack flexDirection="row" gap="12px">
          <Button
            onClick={() => {
              handleCloseDialog();
            }}
          >
            {canDelete ? "No" : "Close"}
          </Button>
          {canDelete ? (
            <Button
              variant="contained"
              onClick={async () => {
                handleCloseDialog();
                typeof callBack === "function"
                  ? await callBack(segment.id)
                  : await deleteSegment(segment.id);
              }}
            >
              Yes
            </Button>
          ) : null}
        </Stack>
      );
      draft.sx = { width: { xs: "90%", sm: 425, md: 550 } };
    });
  };

  return {
    state,
    breadcrumbLinks,
    gotoPage,
    setPageSize,
    onChangeFilter,
    onSearchClear,
    toggleDeleteDialog,
  };
};
